import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getDocumentById } from 'config/firebase';
import { setMaintenanceMode } from 'utils/maintenanceState';
import { ReactComponent as Logo } from 'assets/RewardsLogo.svg';
import maintenanceIcon from 'assets/maintenance-icon.svg';

const FIRESTORE_COLLECTION = process.env.REACT_APP_FIREBASE_MAINTENANCE_COLLECTION;
const FIRESTORE_DOCUMENT_ID = process.env.REACT_APP_FIREBASE_MAINTENANCE_DOCUMENT_ID;

const formatDateRange = (
  start: FirestoreTimestamp | string | null,
  end: FirestoreTimestamp | string | null,
): string => {
  const toDate = (value: FirestoreTimestamp | string | null): Date | null => {
    if (!value) return null;
    if (typeof value === 'string') return new Date(value);
    return new Date(value.seconds * 1000);
  };

  const startDate = toDate(start);
  const endDate = toDate(end);

  if (!startDate && !endDate) return '';

  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formatOpts: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: userTimezone,
  };

  const formatTime = (date: Date): string => date.toLocaleString('en-US', formatOpts);

  if (startDate && endDate) {
    return `${formatTime(startDate)} – ${formatTime(endDate)} (${userTimezone})`;
  }
  if (startDate) return `${formatTime(startDate)} (${userTimezone})`;
  if (endDate) return `Until ${formatTime(endDate)} (${userTimezone})`;
  return '';
};

type MaintenanceGateProps = {
  children: React.ReactNode;
};

const MaintenanceGate: React.FC<MaintenanceGateProps> = ({ children }) => {
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceWindow | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const fetchMaintenanceData = useCallback(async (isInitial = false) => {
    if (!FIRESTORE_COLLECTION || !FIRESTORE_DOCUMENT_ID) {
      if (isInitial) setLoading(false);
      return;
    }

    try {
      const { data } = await getDocumentById<MaintenanceWindow>(
        FIRESTORE_COLLECTION,
        FIRESTORE_DOCUMENT_ID,
      );
      if (data) {
        setMaintenanceData(data);
        setMaintenanceMode(!!data.enabled);
      }
    } catch {
      // silently skip — app loads normally if Firestore is unavailable
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaintenanceData(true);
  }, [fetchMaintenanceData]);

  useEffect(() => {
    fetchMaintenanceData(false);
  }, [location, fetchMaintenanceData]);

  if (loading) return null;

  if (maintenanceData?.enabled) {
    const dateRange = formatDateRange(maintenanceData.start_date, maintenanceData.end_date);

    return (
      <div className="fixed inset-0 bg-[#f0f2f5] z-[9999] flex items-start justify-center overflow-y-auto">
        <div className="flex flex-col items-center text-center max-w-[480px] w-full px-6 py-16">
          <div className="mb-10">
            <Logo width={140} height={40} />
          </div>

          <div className="mb-6">
            <img src={maintenanceIcon} alt="Maintenance" className="w-40 h-40" />
          </div>

          <h4 className="text-[28px] font-medium text-[#00205F] mb-3">{maintenanceData.title}</h4>

          <p className="text-[0.85rem] text-gray-500 mb-6 leading-relaxed">
            {maintenanceData.description}
          </p>

          {dateRange && (
            <span className="text-[0.85rem] text-[#0A72CF] font-semibold">{dateRange}</span>
          )}

          <p className="text-[0.85rem] text-gray-500 mt-8">
            {maintenanceData.support_string}
            <a
              href={`mailto:${maintenanceData.support_email}`}
              className="text-[#0A72CF] font-semibold no-underline hover:underline"
            >
              {maintenanceData.support_email}
            </a>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default React.memo(MaintenanceGate);
